import React, { useState } from 'react';
import { Calendar, Check, CircleAlert, Download, FileText, Globe2, Image as ImageIcon, Package, Pencil, Plus, ScanSearch, Settings, X } from 'lucide-react';
import { ReadinessRing } from '../ui/ReadinessRing';
import { PhFindingSection, PhSection } from './PhFindingSection';
type ReportTab = 'overview' | 'findings' | 'product-data' | 'documents';
const reportTabs: {
  id: ReportTab;
  label: string;
}[] = [{
  id: 'overview',
  label: 'Overview'
}, {
  id: 'findings',
  label: 'Findings'
}, {
  id: 'product-data',
  label: 'Product data'
}, {
  id: 'documents',
  label: 'Documents'
}];
const documentRows = [{
  face: 'Front',
  file: 'Chocolate_Powdered_Drink_Front.pdf',
  size: '1.9 MB'
}, {
  face: 'Back',
  file: 'Chocolate_Powdered_Drink_Back.pdf',
  size: '2.2 MB'
}, {
  face: 'Side',
  file: 'Chocolate_Powdered_Drink_Side.pdf',
  size: '1.4 MB'
}];
const artwork = 'https://cdn.magicpatterns.com/uploads/reUC7PA3gC9W9TJYQNtRNS/Capture_d’ecran_2026-08-18_a_17.15.33.png';
const CLAUSE = 'Standard 1.2.1-8(1)';
const labelSections: PhSection[] = [{
  title: 'Mandatory Labelling',
  checks: '4 checks',
  chips: [],
  verdictNoun: 'PRESENT',
  findings: [{
    name: 'Name of the food',
    status: 'Present',
    confirmation: "Found on the label — 'CHOCOLATE MALT DRINK.'",
    citation: {
      text: CLAUSE
    }
  }, {
    name: 'Lot identification',
    status: 'Present',
    confirmation: 'Found on the label.',
    citation: {
      text: CLAUSE
    }
  }, {
    name: 'Date marking',
    status: 'Present',
    confirmation: 'Found on the label.',
    citation: {
      text: CLAUSE
    }
  }, {
    name: 'Name and address of the supplier',
    status: 'Present',
    confirmation: 'Found on the label — Southbank Foods Pty Ltd, 12 Harbour Rd, Rhodes NSW 2138, Australia.',
    citation: {
      text: CLAUSE
    }
  }]
}, {
  title: 'Ingredient Declaration',
  checks: '2 checks',
  chips: [],
  verdictNoun: 'PRESENT',
  findings: [{
    name: 'Statement of ingredients',
    status: 'Present',
    confirmation: 'Found on the label.',
    citation: {
      text: CLAUSE
    }
  }, {
    name: 'Characterising ingredients/components (percentage labelling)',
    status: 'Present',
    confirmation: 'Found on the label — Total Extract 38% declared.',
    citation: {
      text: CLAUSE
    }
  }]
}, {
  title: 'Nutrition Panel',
  checks: '1 check',
  chips: [],
  verdictNoun: 'PRESENT',
  findings: [{
    name: 'Nutrition information panel',
    status: 'Present',
    confirmation: 'Found on the label.',
    citation: {
      text: CLAUSE
    }
  }]
}, {
  title: 'Advisory & Warning Statements',
  checks: '1 check',
  chips: [],
  verdictNoun: 'PRESENT',
  findings: [{
    name: 'Allergen declarations',
    status: 'Present',
    confirmation: 'Found on the label — declares Gluten, Milk, Soy.',
    citation: {
      text: CLAUSE
    }
  }]
}, {
  title: 'Directions & Storage',
  checks: '1 check',
  chips: [],
  verdictNoun: 'PRESENT',
  findings: [{
    name: 'Directions for use and storage',
    status: 'Present',
    confirmation: 'Found on the label.',
    citation: {
      text: CLAUSE
    }
  }]
}];
const ocrFields: [string, string][] = [['NIP', 'NUTRITION INFORMATION panel is present.'], ['Lot ID', 'The label contains a designated area for batch/lot identification, typically found near the barcode or base.'], ['Date marking', 'The label contains a designated area for date marking (Best Before).'], ['Name of food', 'Chocolate Powdered Drink'], ['Ingredient list', 'Extract of Malt Barley or Malt Barley and Rice (Total Extract 38%), Milk Solids, Sugar, Cocoa#, Choc Malt Blend (Soy), Minerals (Calcium, Iron), Vitamins (C, B3, B6, B2, D, B12).'], ['Supplier details', 'Southbank Foods Pty Ltd, 12 Harbour Rd, Rhodes NSW 2138, Australia.'], ['Characterising pct', 'Total Extract 38%'], ['Directions storage', 'Store in a cool dry place. Use a dry spoon. Replace lid securely after use and consume within 8 weeks.'], ['Allergen declaration', 'Contains Gluten, Milk, Soy.'], ['Nutrition health claim info', 'LOW GI = 36 (Low) made with skim milk'], ['Additional formulated meal replacements supplementary foods', 'Formulated Supplementary Food.']];
export function FoodPackagingLabelTab() {
  const [tab, setTab] = useState<ReportTab>('overview');
  const [showArtwork, setShowArtwork] = useState(false);
  const [enlarged, setEnlarged] = useState(false);
  return <div className="space-y-5" data-id="element-265" data-mp="3uvyq9sk2cvwy">
      <div data-id="element-266" data-mp="3uwqj4vma0fjj">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b968d]" data-id="element-267" data-mp="3ux9s3mty7tlf">Label score by market</p>
        <div className="mt-2 flex flex-wrap items-center gap-2" data-id="element-268" data-mp="cnpqmd2p8ptf">
          <span className="inline-flex items-center gap-2 rounded-full border border-forest-green bg-[#f2fbf4] px-3 py-1.5 font-mono text-sm font-semibold text-evergreen" data-id="element-269" data-mp="7q7nx4f9z6tv">
            <span className="text-base" aria-hidden="true" data-id="element-270" data-mp="1j6xr0eoejij9">
              🇦🇺
            </span>
            AU
            <span className="h-2 w-2 rounded-full bg-forest-green" aria-hidden="true" data-id="element-271" data-mp="1rm3onqs5gdvk" />
            75
          </span>
          <button type="button" className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-[#c8d3c6] px-3 py-1.5 text-sm font-semibold text-[#7d8c80] transition hover:bg-[#f5f8f3]" data-id="element-272" data-mp="1vqbho05n4fzl">
            <Plus className="h-3.5 w-3.5" data-id="element-273" data-mp="2adwjzruoyb6g" />
            Add
          </button>
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-2xl border border-[#dbe4d8] bg-[#f5f8f3] px-4 py-3" data-id="element-274" data-mp="2fchklsduuwvf">
        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-[#718174]" data-id="element-275" data-mp="20od0cjz5mdaj" />
        <p className="text-sm leading-relaxed text-[#5d6f62]" data-id="element-276" data-mp="20m0jka54u8z2">Presence check against the FSANZ Food Standards Code · 3 label faces analysed.</p>
      </div>

      <section className="overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm" aria-labelledby="food-label-heading" data-id="element-277" data-mp="2tbqq9y4fq8l5">
        <div className="flex flex-col gap-4 border-b border-[#e5ebe3] bg-forest-green/5 px-6 py-5 lg:flex-row lg:items-center lg:justify-between" data-id="element-278" data-mp="2y51zg5wpz1g8">
          <div className="flex items-start gap-3" data-id="element-279" data-mp="32zuumeyaks9y">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-forest-green/15 text-forest-green" data-id="element-280" data-mp="32ybhuybugzcx">
              <Check className="h-5 w-5" data-id="element-281" data-mp="2tzzjga4qkx9x" />
            </span>
            <div data-id="element-282" data-mp="2u1iy5waqv09l">
              <p className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-283" data-mp="2p0d4nw99f8z0">Compliant</p>
              <p className="mt-1 text-sm leading-relaxed text-[#54685b]" data-id="element-284" data-mp="2k9mywhwx943s">
                All 9 label elements are present. No action required before this label can ship.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 lg:justify-end" data-id="element-285" data-mp="2abz2lnjo3oji">
            <button type="button" className="inline-flex items-center gap-1.5 rounded-lg border border-[#d6dfd3] bg-white px-3 py-2 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5]" data-id="element-286" data-mp="269vpkuhcobw4">
              <Download className="h-4 w-4" data-id="element-287" data-mp="grotue93dm39" />
              Download PDF
            </button>
            <button type="button" className="inline-flex items-center gap-1.5 rounded-lg bg-forest-green px-3 py-2 text-sm font-semibold text-white transition hover:bg-evergreen" data-id="element-288" data-mp="lqrxbu9fbk94">
              Scan another →
            </button>
            <button type="button" className="inline-flex items-center gap-1.5 rounded-lg border border-[#d6dfd3] bg-white px-3 py-2 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5]" data-id="element-289" data-mp="3u7wgu6pdlzg8">
              <Pencil className="h-4 w-4" data-id="element-290" data-mp="7259f45qdk6a" />
              Review report
            </button>
            <button type="button" aria-label="Report settings" className="rounded-lg border border-[#d6dfd3] bg-white p-2 text-[#637667] transition hover:bg-[#f7faf5]" data-id="element-291" data-mp="buf5l1xfvgh0">
              <Settings className="h-4 w-4" data-id="element-292" data-mp="3fgxevej1q95m" />
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-6 p-6 lg:flex-row lg:items-center" data-id="element-293" data-mp="28vo8age7y1tm">
          <div className="flex shrink-0 flex-col items-center gap-1" data-id="element-294" data-mp="23xmoft8yxyh3">
            <ReadinessRing score={75} size={100} label="Label compliance score" data-id="element-295" data-mp="2hwm2y82rowzj" />
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b968d]" data-id="element-296" data-mp="2duip5csmlgb3">Score</p>
          </div>
          <img src={artwork} alt="Chocolate Powdered Drink label artwork" className="h-24 w-24 shrink-0 rounded-xl border border-[#e1e8df] object-cover" data-id="element-297" data-mp="2rtifqpzp859w" />
          <div className="min-w-0 flex-1" data-id="element-298" data-mp="31j1w9ozxvn1l">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b968d]" data-id="element-299" data-mp="31m66b3035d06">Product</p>
            <h2 id="food-label-heading" className="mt-1 font-[Figtree] text-2xl font-semibold tracking-tight text-evergreen" data-id="element-300" data-mp="3akrnatpwsjyu">
              Chocolate Powdered Drink
            </h2>
            <div className="mt-3 flex flex-wrap gap-2" data-id="element-301" data-mp="35jluyx05uypu">
              <Pill data-id="element-302" data-mp="3p8iabzid5c3u">Southbank Foods</Pill>
              <Pill icon={<Package className="h-3.5 w-3.5" data-id="element-304" data-mp="3p5nkttkht13k" />} data-id="element-303" data-mp="3p5duu5zg6txv">Food · Chocolate and cocoa products</Pill>
              <Pill icon={<Globe2 className="h-3.5 w-3.5" data-id="element-306" data-mp="3g6sgyo4x833f" />} data-id="element-305" data-mp="3u47d0bpoj94d">Australia</Pill>
              <Pill icon={<Calendar className="h-3.5 w-3.5" data-id="element-308" data-mp="3g8v490eovi4g" />} data-id="element-307" data-mp="3g6ird1nsfxy9">7 Jul 2026</Pill>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2 lg:self-start" data-id="element-309" data-mp="3kagppa5ah4q9">
            <span className="rounded-full border border-[#d8e1d5] bg-[#fbfdf9] px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-[#56685c]" data-id="element-310" data-mp="36fed645xsh4h">
              3 face(s)
            </span>
            <button type="button" className="inline-flex items-center gap-1.5 rounded-lg border border-[#d6dfd3] bg-white px-3 py-2 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5]" data-id="element-311" data-mp="2wkmbao5bn83w">
              <ScanSearch className="h-4 w-4" data-id="element-312" data-mp="2shpr4t04v3xu" />
              New analysis
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 divide-[#e5ebe3] border-t border-[#e5ebe3] sm:grid-cols-3 sm:divide-x" data-id="element-313" data-mp="3bqak0eazu30a">
          <Metric icon={<Check className="h-4 w-4 text-forest-green" data-id="element-315" data-mp="3grq3w31fq3h3" />} value="9" caption="Present" tone="text-forest-green" data-id="element-314" data-mp="3grzunsub0enu" />
          <Metric icon={<CircleAlert className="h-4 w-4 text-[#adb8ae]" data-id="element-317" data-mp="37pqmvaqf6ze5" />} value="0" caption="Missing" tone="text-[#8b968d]" data-id="element-316" data-mp="32t04uhv21tq3" />
          <Metric icon={<CircleAlert className="h-4 w-4 text-[#adb8ae]" data-id="element-319" data-mp="3vf2s5zimmnb6" />} value="0" caption="Needs review" tone="text-[#8b968d]" data-id="element-318" data-mp="3veszu5a4042b" />
        </div>
      </section>

      <div className="border-b border-[#dce3da]" data-id="element-320" data-mp="3dmob2i9d795">
        <nav className="flex flex-wrap gap-1" aria-label="Label report sections" data-id="element-321" data-mp="3lqb3c08v4j4h">
          {reportTabs.map(reportTab => <button key={reportTab.id} type="button" onClick={() => setTab(reportTab.id)} aria-current={tab === reportTab.id ? 'page' : undefined} className={`inline-flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition ${tab === reportTab.id ? 'border-forest-green text-forest-green' : 'border-transparent text-[#718174] hover:text-evergreen'}`} data-id="element-322" data-mp="3qn1k6pshriqy">
              {reportTab.label}
            </button>)}
        </nav>
      </div>

      {tab === 'overview' && <div className="grid gap-4 lg:grid-cols-3" data-id="element-323" data-mp="115tcbtac39vv">
          <section className="rounded-2xl border border-[#dce3da] bg-white p-5 shadow-sm lg:col-span-2" aria-labelledby="food-top-issues" data-id="element-324" data-mp="dep10lf92b4g">
            <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-325" data-mp="9folbbs2jolo">
              <h3 id="food-top-issues" className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-326" data-mp="nefxutra927p">
                Top issues
              </h3>
              <button type="button" onClick={() => setTab('findings')} className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-forest-green transition hover:underline" data-id="element-327" data-mp="1ystfvhzcunft">
                View all →
              </button>
            </div>
            <div className="flex flex-col items-center gap-3 py-10 text-center" data-id="element-328" data-mp="25v8f5ve3syes">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-forest-green/10 text-forest-green" data-id="element-329" data-mp="2avclislascra">
                <Check className="h-5 w-5" data-id="element-330" data-mp="2ft4nygjkjh9q" />
              </span>
              <p className="text-sm font-semibold text-evergreen" data-id="element-331" data-mp="1i6fi1vlqwkil">No issues found — every label element is present.</p>
            </div>
          </section>

          <section className="rounded-2xl border border-[#dce3da] bg-white p-5 shadow-sm" aria-labelledby="food-sections" data-id="element-332" data-mp="1n61qzsujjell">
            <h3 id="food-sections" className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-333" data-mp="1s2ijez8rwf4h">
              Sections
            </h3>
            <ul className="mt-3 divide-y divide-[#f0f3ee]" data-id="element-334" data-mp="zgpmv7e2lwq5">
              {labelSections.map(section => <li key={section.title} data-id="element-335" data-mp="14dz96e65xs9r">
                  <button type="button" onClick={() => setTab('findings')} className="flex w-full items-center justify-between gap-3 rounded-lg px-2 py-2.5 text-left transition hover:bg-[#f7faf5]" data-id="element-336" data-mp="14cxulzpxunna">
                    <span className="text-sm font-medium text-evergreen" data-id="element-337" data-mp="mvqzpqqyj69b">{section.title}</span>
                    <span className="shrink-0 rounded-full bg-forest-green/10 px-2 py-0.5 font-mono text-[10px] font-semibold text-forest-green" data-id="element-338" data-mp="hxpfv3lpj2ws">
                      0
                    </span>
                  </button>
                </li>)}
            </ul>
          </section>
        </div>}

      {tab === 'findings' && <section aria-label="Findings" data-id="element-339" data-mp="3vddo1o7n17zy">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-340" data-mp="3vegczqleh0d8">
            <h2 className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-341" data-mp="3qcr0s7mfygnk">Findings</h2>
            <button type="button" onClick={() => setShowArtwork(open => !open)} aria-expanded={showArtwork} className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-lg border border-[#d6dfd3] bg-white px-3 py-2 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5] sm:self-auto" data-id="element-342" data-mp="3mdi9d9eviuv6">
              <ImageIcon className="h-4 w-4" data-id="element-343" data-mp="a6gywbnzowue" />
              {showArtwork ? 'Hide artwork' : 'View label artwork'}
            </button>
          </div>

          {showArtwork ? <div className="mt-4 flex flex-col gap-4 lg:flex-row" data-id="element-344" data-mp="16as18msqv47">
              <div className="max-h-[640px] overflow-y-auto lg:w-2/5 lg:shrink-0" data-id="element-345" data-mp="17lwlao2g6wd">
                <p className="text-xs text-[#8b968d]" data-id="element-346" data-mp="3jdi1tdihka5y">Face 1 of 3 · Chocolate Powdered Drink artwork</p>
                <button type="button" onClick={() => setEnlarged(true)} className="group relative mt-2 block w-full overflow-hidden rounded-xl border border-[#e1e8df] bg-[#f6f8f5]" data-id="element-347" data-mp="3ne0utaw47m9q">
                  <img src={artwork} alt="Chocolate Powdered Drink label artwork" className="aspect-[4/3] w-full object-contain" data-id="element-348" data-mp="363omale8pio1" />
                  <span className="absolute bottom-2 right-2 rounded-full bg-evergreen/85 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-white opacity-0 transition group-hover:opacity-100" data-id="element-349" data-mp="323w9fwhtcqcv">
                    Click to enlarge
                  </span>
                </button>
              </div>
              <div className="max-h-[640px] flex-1 space-y-3 overflow-y-auto pr-1" data-id="element-350" data-mp="3pua5rz7flbd9">
                {labelSections.map(section => <PhFindingSection key={section.title} section={section} defaultOpen={section.title === 'Mandatory Labelling'} data-id="element-351" data-mp="2nznu10gt9zb" />)}
              </div>
            </div> : <div className="mt-4 space-y-3" data-id="element-352" data-mp="24eb2hecuiot2">
              {labelSections.map(section => <PhFindingSection key={section.title} section={section} defaultOpen={section.title === 'Mandatory Labelling'} data-id="element-353" data-mp="1zfzw5dlrewfl" />)}
            </div>}

          {enlarged && <div role="dialog" aria-modal="true" aria-label="Label artwork" className="fixed inset-0 z-50 flex items-center justify-center bg-evergreen/70 p-6" onClick={() => setEnlarged(false)} data-id="element-354" data-mp="8bg7o55addxp">
              <div className="w-full max-w-4xl" onClick={event => event.stopPropagation()} data-id="element-355" data-mp="mbh4n9ifk17w">
                <div className="flex items-center justify-between pb-3" data-id="element-356" data-mp="m9msajl85hff">
                  <p className="font-mono text-xs font-semibold uppercase tracking-[0.14em] text-white" data-id="element-357" data-mp="r8pp5gr1whcb">Chocolate Powdered Drink artwork</p>
                  <button type="button" onClick={() => setEnlarged(false)} className="rounded-lg bg-white/15 p-2 text-white hover:bg-white/25" aria-label="Close artwork preview" data-id="element-358" data-mp="1103vo8hf6hey">
                    <X className="h-4 w-4" data-id="element-359" data-mp="19yp8ro44h48c" />
                  </button>
                </div>
                <img src={artwork} alt="Label artwork, enlarged" className="w-full rounded-xl bg-white object-contain" data-id="element-360" data-mp="2ugzt3k1ct0bm" />
              </div>
            </div>}
        </section>}

      {tab === 'findings' && <section className="rounded-2xl border border-[#dce3da] bg-white px-6 py-5 shadow-sm" aria-labelledby="required-statements-heading" data-id="element-361" data-mp="2gjkxfxkibwb7">
          <h2 id="required-statements-heading" className="font-[Figtree] text-xl font-semibold text-evergreen" data-id="element-362" data-mp="3d8ai1vnir30v">
            Required statements
          </h2>
          <p className="mt-1 text-sm text-[#718174]" data-id="element-363" data-mp="34h0h3ja644m5">Layer A exact statements the label must carry.</p>
          <p className="mt-4 text-sm text-[#96a098]" data-id="element-364" data-mp="2zifghir07ix6">No mandatory statements were evaluated.</p>
        </section>}

      {tab === 'product-data' && <section className="rounded-2xl border border-[#dce3da] bg-white shadow-sm" aria-labelledby="ocr-heading" data-id="element-365" data-mp="ti0w8ehogy6">
          <div className="border-b border-[#e5ebe3] px-6 py-5" data-id="element-366" data-mp="3s502e3opn2tc">
            <h2 id="ocr-heading" className="font-[Figtree] text-xl font-semibold text-evergreen" data-id="element-367" data-mp="30hp00rdhazd7">
              OCR evidence
            </h2>
            <p className="mt-1 text-sm text-[#718174]" data-id="element-368" data-mp="2ql3ve75dfq9w">First non-empty value across faces, per field.</p>
          </div>
          <ul className="divide-y divide-[#edf0eb]" data-id="element-369" data-mp="2vjniiavpb83u">
            {ocrFields.map(([label, value]) => <li key={label} className="px-6 py-4" data-id="element-370" data-mp="3j9hsmkomyike">
                <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8b968d]" data-id="element-371" data-mp="3j7oi2uawje74">{label}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-evergreen" data-id="element-372" data-mp="3a932n80q8fak">{value}</p>
              </li>)}
          </ul>
        </section>}

      {tab === 'documents' && <section className="overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm" aria-labelledby="documents-heading" data-id="element-373" data-mp="1ytxo96r8gsb4">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#e5ebe3] px-6 py-5" data-id="element-374" data-mp="22wkl5m2rh3f6">
            <h2 id="documents-heading" className="font-[Figtree] text-xl font-semibold text-evergreen" data-id="element-375" data-mp="1vap31pvmoj6b">
              Artwork
            </h2>
            <button type="button" className="inline-flex items-center gap-1.5 rounded-lg border border-[#d6dfd3] bg-white px-3 py-2 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5]" data-id="element-376" data-mp="25a68q7avqx2a">
              <Plus className="h-4 w-4" data-id="element-377" data-mp="1cmk4qxxqpqew" />
              Add artwork
            </button>
          </div>
          <div className="overflow-x-auto" data-id="element-378" data-mp="17nh3lokvs4c7">
            <table className="w-full min-w-[640px] text-left" data-id="element-379" data-mp="17kl2xshnf1k2">
              <thead className="border-b border-[#edf0eb] bg-[#fbfdf9]" data-id="element-380" data-mp="1mcvamq0zi9of">
                <tr className="font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-[#758578]" data-id="element-381" data-mp="1hkbvhomnf6hi">
                  <th className="px-6 py-3" data-id="element-382" data-mp="ttzja1kdenl4">File</th>
                  <th className="px-6 py-3" data-id="element-383" data-mp="ovxzfef4ek8l">Face</th>
                  <th className="px-6 py-3" data-id="element-384" data-mp="ov50gzmekz10">Type</th>
                  <th className="px-6 py-3" data-id="element-385" data-mp="272sgwa3ky5zh">Size</th>
                  <th className="px-6 py-3 text-right" data-id="element-386" data-mp="273jyui49857a">Analysed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#edf0eb]" data-id="element-387" data-mp="225s83rfcip4o">
                {documentRows.map(row => <tr key={row.file} data-id="element-388" data-mp="2fxgmk1d2hpot">
                    <td className="px-6 py-4" data-id="element-389" data-mp="2b43vlkkx16sw">
                      <span className="flex items-center gap-2 text-sm font-semibold text-evergreen" data-id="element-390" data-mp="2b3cbp70okxig">
                        <FileText className="h-4 w-4 text-[#738478]" data-id="element-391" data-mp="2prp1tnd37m51" />
                        {row.file}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#5d6f62]" data-id="element-392" data-mp="2yuhsppicxvuc">{row.face}</td>
                    <td className="px-6 py-4 font-mono text-xs text-[#718174]" data-id="element-393" data-mp="2us39h4as0u2j">PDF</td>
                    <td className="px-6 py-4 font-mono text-xs text-[#718174]" data-id="element-394" data-mp="14g6z73qtidi8">{row.size}</td>
                    <td className="px-6 py-4 text-right font-mono text-xs text-[#718174]" data-id="element-395" data-mp="10eljlamwfl4c">7 Jul 2026</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </section>}
    </div>;
}
function Pill({
  children,
  icon
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return <span className="inline-flex items-center gap-1.5 rounded-full border border-[#dce3da] bg-[#f9fbf8] px-2.5 py-1 text-xs font-medium text-[#5d6f62]" data-id="element-396" data-mp="gjso93ggp119">
      {icon}
      {children}
    </span>;
}
function Metric({
  icon,
  value,
  caption,
  tone
}: {
  icon: React.ReactNode;
  value: string;
  caption: string;
  tone: string;
}) {
  return <div className="flex items-center gap-3 px-6 py-4" data-id="element-397" data-mp="bjo1wwsvhc34">
      {icon}
      <div data-id="element-398" data-mp="3tsox5n3u3fwx">
        <p className={`font-[Figtree] text-2xl font-semibold leading-none ${tone}`} data-id="element-399" data-mp="3tsygwsyacqu6">{value}</p>
        <p className="mt-1 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8b968d]" data-id="element-400" data-mp="2lm5yvnzx9mp">{caption}</p>
      </div>
    </div>;
}